import React, { useEffect, useState } from 'react';
import { Leaf, Lightbulb, Eye, Scale, Shield, Award, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { fetchPublicSettings } from '../services/publicContentService';

const Values = ({ theme = 'light' }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isAr = language === 'ar';
  const isDark = theme === 'dark';

  // CMS-driven intro paragraph (fallbacks kept intact for the static default).
  const [cmsIntro, setCmsIntro] = useState(null); // { ar, en } | null
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('company_info').then((v) => {
      if (!alive) return;
      const val = v && v.values;
      if (val && (val.ar || val.en)) setCmsIntro(val);
    });
    return () => { alive = false; };
  }, []);

  // CMS-driven values card content — falls back to translations. Icons and
  // gradients stay static (design constraint).
  const [cmsCards, setCmsCards] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('values_cards').then((v) => {
      if (!alive) return;
      const list = Array.isArray(v?.items) ? v.items : [];
      setCmsCards(list);
    });
    return () => { alive = false; };
  }, []);

  const pickTitle = (idx, fallback) => {
    const it = cmsCards[idx] || {};
    const v = isAr ? (it.title_ar || it.title_en) : (it.title_en || it.title_ar);
    return (v && v.trim()) || fallback;
  };
  const pickDesc = (idx, fallback) => {
    const it = cmsCards[idx] || {};
    const v = isAr ? (it.description_ar || it.description_en) : (it.description_en || it.description_ar);
    return (v && v.trim()) || fallback;
  };

  const values = [
    {
      id: 'sustainability',
      title: pickTitle(0, t.sustainability),
      icon: Leaf,
      gradient: 'from-cyan-400 via-sky-400 to-blue-400',
      darkText: 'text-cyan-300',
      lightText: 'text-cyan-700',
      desc: pickDesc(0, t.sustainabilityFull),
    },
    {
      id: 'transparency',
      title: pickTitle(1, t.transparency),
      icon: Eye,
      gradient: 'from-emerald-400 via-teal-400 to-green-500',
      darkText: 'text-emerald-300',
      lightText: 'text-emerald-700',
      desc: pickDesc(1, t.transparencyFull),
    },
    {
      id: 'integrity',
      title: pickTitle(2, t.integrity),
      icon: Shield,
      gradient: 'from-amber-400 via-orange-400 to-amber-500',
      darkText: 'text-amber-300',
      lightText: 'text-amber-700',
      desc: pickDesc(2, t.integrityFull),
    },
    {
      id: 'quality',
      title: pickTitle(3, t.quality),
      icon: Award,
      gradient: 'from-cyan-400 via-sky-400 to-blue-400',
      darkText: 'text-cyan-300',
      lightText: 'text-cyan-700',
      desc: pickDesc(3, t.qualityFull),
    },
    {
      id: 'justice',
      title: pickTitle(4, t.justice),
      icon: Scale,
      gradient: 'from-emerald-400 via-teal-400 to-green-500',
      darkText: 'text-emerald-300',
      lightText: 'text-emerald-700',
      desc: pickDesc(4, t.justiceFull),
    },
    {
      id: 'innovation',
      title: pickTitle(5, t.innovation),
      icon: Lightbulb,
      gradient: 'from-amber-400 via-orange-400 to-amber-500',
      darkText: 'text-amber-300',
      lightText: 'text-amber-700',
      desc: pickDesc(5, t.innovationFull),
    },
  ];

  return (
    <section
      id="values"
      dir={isAr ? 'rtl' : 'ltr'}
      className="relative py-32 overflow-hidden transition-colors duration-700"
    >
      {/* Theme-aware base */}
      {isDark ? (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-cyan-50/60 to-emerald-50/40" />
      )}

      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: isDark
            ? 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.4) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Soft ambient glows */}
      <div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none"
        style={{
          background: isDark ? 'rgba(34,211,238,0.15)' : 'rgba(34,211,238,0.18)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none"
        style={{
          background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.15)',
        }}
      />

      <div className="relative container mx-auto px-4">
        {/* Editorial Header — asymmetric */}
        <div className="grid lg:grid-cols-12 gap-8 mb-20 max-w-7xl mx-auto items-end">
          <div className={`lg:col-span-7 ${isAr ? 'text-right' : 'text-left'}`}>
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 ${
              isDark
                ? 'bg-cyan-500/10 border border-cyan-500/30'
                : 'bg-cyan-600/10 border border-cyan-600/30'
            }`}>
              <Sparkles className={`w-4 h-4 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`} />
              <span className={`text-xs tracking-[0.3em] uppercase ${
                isDark ? 'text-cyan-200' : 'text-cyan-800'
              }`}>
                {isAr ? 'هويتنا' : 'Our Identity'}
              </span>
            </div>

            <h2 className={`text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {isAr ? (
                <>
                  ستة <span className={`bg-clip-text text-transparent ${
                    isDark
                      ? 'bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-300'
                      : 'bg-gradient-to-r from-cyan-700 via-emerald-600 to-amber-600'
                  }`}>قيم</span>
                  <br />
                  تقودُ كل قرار.
                </>
              ) : (
                <>
                  Six <span className={`bg-clip-text text-transparent ${
                    isDark
                      ? 'bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-300'
                      : 'bg-gradient-to-r from-cyan-700 via-emerald-600 to-amber-600'
                  }`}>values</span>
                  <br />
                  guiding every decision.
                </>
              )}
            </h2>
          </div>

          <div className={`lg:col-span-5 ${isAr ? 'lg:text-right' : 'lg:text-left'}`}>
            <div className={`flex items-center gap-3 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
              <div className={`h-px flex-1 ${
                isDark ? 'bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent' : 'bg-gradient-to-r from-transparent via-cyan-600/40 to-transparent'
              }`} />
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isDark
                  ? 'bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-400/30'
                  : 'bg-gradient-to-br from-cyan-100 to-emerald-100 border border-cyan-300'
              }`}>
                <Leaf className={`w-6 h-6 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`} />
              </div>
              <div className={`h-px flex-1 ${
                isDark ? 'bg-gradient-to-l from-transparent via-cyan-400/40 to-transparent' : 'bg-gradient-to-l from-transparent via-cyan-600/40 to-transparent'
              }`} />
            </div>
            <p className={`text-base md:text-lg leading-relaxed ${
              isDark ? 'text-slate-300' : 'text-slate-600'
            }`}
               data-testid="values-intro">
              {(cmsIntro && (isAr ? cmsIntro.ar : cmsIntro.en)) || (isAr
                ? 'في شركة التوازن البيئي، لا نرى القيم كشعارات على جدار — بل كبوصلة عملية تُوجّه كل مشروع، وكل توصية، وكل علاقة مع شركائنا.'
                : 'At EBC, our values are not slogans on a wall — they are a practical compass guiding every project, every recommendation, every partnership.')}
            </p>
          </div>
        </div>

        {/* Bento grid of values */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 max-w-7xl mx-auto">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <article
                key={value.id}
                data-testid={`value-card-${value.id}`}
                className={`group relative overflow-hidden rounded-3xl p-7 lg:p-8 animate-fade-in-up
                            transition-all duration-500 cursor-default
                            ${isDark
                              ? 'bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                              : 'bg-white/80 border border-slate-200 hover:bg-white hover:border-slate-300 shadow-sm hover:shadow-2xl'}
                            backdrop-blur-xl hover:-translate-y-1`}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {/* Top gradient bar */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${value.gradient} opacity-70`} />

                {/* Hover gradient glow */}
                <div className={`absolute -inset-px rounded-3xl bg-gradient-to-br ${value.gradient}
                                opacity-0 group-hover:opacity-[0.08] blur-2xl transition-opacity duration-500`} />

                <div className="relative">
                  {/* Icon + number row */}
                  <div className={`flex items-start justify-between mb-6 ${isAr ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${value.gradient}
                                    flex items-center justify-center shadow-lg
                                    transition-all duration-500
                                    group-hover:scale-110 group-hover:rotate-6`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <span className={`text-xs tracking-[0.3em] font-mono uppercase ${
                      isDark ? 'text-cyan-300/70' : 'text-cyan-700/80'
                    }`}>
                      {String(index + 1).padStart(2, '0')} / 06
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className={`text-2xl lg:text-3xl font-bold mb-3 tracking-tight ${
                    isDark ? 'text-white' : 'text-slate-900'
                  }`}>
                    {value.title}
                  </h3>

                  {/* Underline accent */}
                  <div className={`h-0.5 w-12 mb-4 bg-gradient-to-r ${value.gradient}
                                  transition-all duration-500 group-hover:w-20`} />

                  {/* Description */}
                  <p className={`text-[15px] leading-relaxed ${
                    isDark ? 'text-slate-300/90' : 'text-slate-600'
                  }`}
                     style={{ direction: isAr ? 'rtl' : 'ltr' }}>
                    {value.desc}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom signature line */}
        <div className="mt-16 text-center animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div className={`inline-flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
            <div className={`h-px w-16 ${isDark ? 'bg-cyan-400/30' : 'bg-cyan-700/30'}`} />
            <span className={`text-sm tracking-[0.4em] uppercase ${
              isDark ? 'text-cyan-300/70' : 'text-cyan-800/70'
            }`}>
              {isAr ? 'التوازن البيئي • EBC' : 'Environmental Balance • EBC'}
            </span>
            <div className={`h-px w-16 ${isDark ? 'bg-cyan-400/30' : 'bg-cyan-700/30'}`} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Values;
